/**
 * Sport Bloc design-system primitives (React layer).
 * Ported from the reference `app/components.jsx` (SPEC §1.6), using the
 * existing `sb-*` CSS classes in admin-sb.css. The class-based primitives
 * (Button=.sb-btn, Card=.sb-card, Chipset=.sb-chipset, Bar=.sb-bar, Modal/
 * Drawer) are applied directly via className; this file supplies the ones the
 * codebase was missing as reusable components: Sparkline, Money, StatusBadge,
 * SBBadge, SBAvatar.
 */

/** Inline SVG sparkline — ~120×36, area + line. Reference components.jsx:120-134. */
export function Sparkline({ data = [], color = 'var(--brand)', height = 36 }) {
  if (!data || data.length < 2) return <svg className="sb-spark" style={{ width: '100%', height }} />;
  const w = 120, h = height;
  const max = Math.max(...data), min = Math.min(...data);
  const range = max - min || 1;
  const step = w / (data.length - 1);
  const pts = data.map((d, i) => [i * step, h - ((d - min) / range) * (h - 4) - 2]);
  const path = pts.map((p, i) => (i === 0 ? `M${p[0]},${p[1]}` : `L${p[0]},${p[1]}`)).join(' ');
  const area = `${path} L${w},${h} L0,${h} Z`;
  return (
    <svg className="sb-spark" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none"
         style={{ width: '100%', height: h, color }}>
      <path d={area} fill={color} fillOpacity="0.10" />
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

/** Pill badge with leading dot. Tones: ok | warn | bad | info | plain. */
export function SBBadge({ tone = 'plain', children, ...rest }) {
  return <span className={`sb-badge ${tone}`} {...rest}>{children}</span>;
}

/** Status → badge variant mapping. Reference components.jsx:182-187. */
export function StatusBadge({ status }) {
  if (status === 'confirmed') return <SBBadge tone="ok">Confirmed</SBBadge>;
  if (status === 'pending')   return <SBBadge tone="warn">Pending</SBBadge>;
  if (status === 'cancelled') return <SBBadge tone="bad">Cancelled</SBBadge>;
  return <SBBadge>{status}</SBBadge>;
}

/** Avatar circle with initials; rotates through 5 colour ramps (c0–c4). */
export function SBAvatar({ initials, color = 0, size = 32 }) {
  return (
    <div className={`sb-av c${color}`} style={{ width: size, height: size, fontSize: size * 0.36 }}>
      {initials}
    </div>
  );
}

/**
 * Money formatter — INR with a small ₹ glyph, optional due/paid sub-line.
 * Reference components.jsx:163-179.
 */
export function Money({ value = 0, due = 0, paid = 0, status }) {
  const v = Math.abs(value);
  return (
    <div className="sb-bk-money sb-num">
      <div><span style={{ color: 'var(--ink-3)', marginRight: 2 }}>₹</span>{v.toLocaleString('en-IN')}</div>
      {due > 0 && status !== 'cancelled' && (
        <div className="sb-bk-due">₹{due.toLocaleString('en-IN')} due</div>
      )}
      {due === 0 && paid > 0 && status !== 'cancelled' && (
        <div className="sb-bk-paid">paid</div>
      )}
    </div>
  );
}

/** Plain INR string helper (no markup). */
export const formatMoney = (n = 0) => `₹${Math.abs(Number(n) || 0).toLocaleString('en-IN')}`;
